(() => {
  if (window.__MF_DESIGNER_DIRECT_RESIZE_V1__) return;
  window.__MF_DESIGNER_DIRECT_RESIZE_V1__ = true;

  const TARGET = [
    '[data-mfs] canvas[data-canvas]',
    '[data-mfh] canvas[data-canvas]',
    '[data-mfp] canvas[data-canvas]',
    '[data-mf-beanie-canvas]',
    '[data-mf-trucker-canvas]',
    '[data-mf-sl] canvas[data-canvas]'
  ].join(',');

  const ROOTS = '[data-mfs],[data-mfh],[data-mfp],[data-mf-beanie-shell],[data-mf-trucker-shell],[data-mf-sl]';
  const states = new WeakMap();
  const activeStates = new Set();

  const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
  const normColor = (v) => String(v || '').toLowerCase().replace(/\s+/g, '');
  const isTarget = (canvas) => canvas instanceof HTMLCanvasElement && canvas.matches(TARGET);

  function rootFor(canvas) {
    return canvas?.closest?.(ROOTS) || null;
  }

  function imageSelected(root) {
    if (!root) return false;

    if (root.matches('[data-mfs],[data-mfh],[data-mfp]')) {
      const edit = root.querySelector('[data-edit]');
      const label = root.querySelector('[data-selected]')?.textContent?.trim() || '';
      return !!edit && !edit.hidden && /^Bild\b/i.test(label);
    }

    if (root.matches('[data-mf-beanie-shell]')) {
      const layer = root.querySelector('[data-mf-beanie-layer].is-active')?.dataset.mfBeanieLayer;
      const name = root.querySelector('[data-mf-beanie-file-name]')?.textContent?.trim() || '';
      return layer === 'image' && !!name && name !== 'Noch keine Datei ausgewählt';
    }

    if (root.matches('[data-mf-trucker-shell]')) {
      const layer = root.querySelector('[data-mf-trucker-layer].is-active')?.dataset.mfTruckerLayer;
      const name = root.querySelector('[data-mf-trucker-file-name]')?.textContent?.trim() || '';
      return layer === 'image' && !!name && name !== 'Noch keine Datei ausgewählt';
    }

    if (root.matches('[data-mf-sl]')) {
      const layer = root.querySelector('[data-layer].on')?.dataset.layer;
      const name = root.querySelector('[data-fn]')?.textContent?.trim() || '';
      return (!layer || layer === 'image') && !!name && name !== 'Noch keine Datei ausgewählt';
    }

    return false;
  }

  function scaleRangeFor(root) {
    if (!root) return null;
    if (root.matches('[data-mf-beanie-shell]')) return root.querySelector('[data-mf-beanie-image-scale]');
    if (root.matches('[data-mf-trucker-shell]')) return root.querySelector('[data-mf-trucker-image-scale]');
    return root.querySelector('[data-scale]');
  }

  function isSelectionStroke(ctx, root) {
    if (!ctx || !root || ctx.globalAlpha <= 0.05) return false;
    const c = normColor(ctx.strokeStyle);

    if (root.matches('[data-mfs],[data-mfh],[data-mfp]')) {
      return c === '#ff6a00' || c === 'rgb(255,106,0)' || c === 'rgba(255,106,0,1)';
    }

    const orange = c === '#f58220' || c === 'rgb(245,130,32)' || c === 'rgba(245,130,32,1)';
    return orange && Number(ctx.lineWidth) >= 5.5;
  }

  function transformPoint(m, x, y) {
    return {
      x: m.a * x + m.c * y + m.e,
      y: m.b * x + m.d * y + m.f
    };
  }

  function toClient(canvas, p) {
    const r = canvas.getBoundingClientRect();
    return {
      x: r.left + (p.x / canvas.width) * r.width,
      y: r.top + (p.y / canvas.height) * r.height
    };
  }

  function makeHandle(st, corner) {
    const h = document.createElement('button');
    h.type = 'button';
    h.className = `mf-mouse-resize-handle mf-mouse-resize-handle--${corner}`;
    h.dataset.corner = corner;
    h.setAttribute('aria-label', 'Motiv proportional größer oder kleiner ziehen');
    h.title = 'Ziehen zum proportionalen Vergrößern oder Verkleinern';
    h.innerHTML = '<span aria-hidden="true"></span>';
    st.host.appendChild(h);
    h.addEventListener('pointerdown', (e) => beginResize(st, e, corner));
    return h;
  }

  function ensureState(canvas) {
    let st = states.get(canvas);
    if (st) return st;

    const root = rootFor(canvas);
    if (!root) return null;
    const host = canvas.closest('dialog') || root;

    st = {
      canvas, root, host,
      handles: {},
      info: null,
      gen: 0,
      capturedGen: -1,
      resizing: false,
      realPointerId: null,
      centerClient: null,
      startDistance: 0,
      startValue: 100,
      slider: null,
      controller: null,
      activeCorner: null
    };

    ['nw', 'ne', 'se', 'sw'].forEach((corner) => {
      st.handles[corner] = makeHandle(st, corner);
    });

    states.set(canvas, st);
    activeStates.add(st);
    return st;
  }

  function setHandlePos(handle, point) {
    if (!handle || !point) return;
    handle.style.left = `${point.x}px`;
    handle.style.top = `${point.y}px`;
  }

  function updateHandles(st) {
    if (!st || !st.info || window.innerWidth <= 760) return hide(st);
    if (!st.canvas.isConnected || !st.root.isConnected || !imageSelected(st.root)) return hide(st);

    const r = st.canvas.getBoundingClientRect();
    if (!r.width || !r.height) return hide(st);

    Object.entries(st.info.corners).forEach(([corner, p]) => {
      const h = st.handles[corner];
      if (!h) return;
      const client = toClient(st.canvas, p);
      setHandlePos(h, client);
      h.style.display = 'grid';
    });
  }

  function hide(st) {
    if (!st || st.resizing) return;
    Object.values(st.handles || {}).forEach((h) => {
      if (h) h.style.display = 'none';
    });
  }

  function captureSelection(ctx, x, y, w, h) {
    const canvas = ctx.canvas;
    if (!isTarget(canvas)) return;

    const root = rootFor(canvas);
    if (!root || !imageSelected(root) || !isSelectionStroke(ctx, root)) return;

    const st = ensureState(canvas);
    if (!st) return;

    const m = ctx.getTransform();
    st.info = {
      center: transformPoint(m, x + w / 2, y + h / 2),
      corners: {
        nw: transformPoint(m, x, y),
        ne: transformPoint(m, x + w, y),
        se: transformPoint(m, x + w, y + h),
        sw: transformPoint(m, x, y + h)
      }
    };
    st.capturedGen = st.gen;
    updateHandles(st);
  }

  const proto = window.CanvasRenderingContext2D?.prototype;
  if (!proto) return;

  if (!proto.__mfDirectResizeStrokePatchedV1) {
    proto.__mfDirectResizeStrokePatchedV1 = true;
    const previousStrokeRect = proto.strokeRect;
    proto.strokeRect = function(x, y, w, h) {
      try { captureSelection(this, x, y, w, h); } catch (_) {}
      return previousStrokeRect.apply(this, arguments);
    };
  }

  if (!proto.__mfDirectResizeClearPatchedV1) {
    proto.__mfDirectResizeClearPatchedV1 = true;
    const previousClearRect = proto.clearRect;
    proto.clearRect = function(x, y, w, h) {
      const canvas = this.canvas;
      let st = null;
      let gen = -1;

      if (isTarget(canvas)) {
        st = ensureState(canvas);
        if (st) {
          st.gen += 1;
          gen = st.gen;
        }
      }

      const out = previousClearRect.apply(this, arguments);

      if (st) {
        requestAnimationFrame(() => {
          if (!st.resizing && st.gen === gen && st.capturedGen !== gen) hide(st);
        });
      }
      return out;
    };
  }

  function finishResize(st, ev) {
    if (!st?.resizing) return;
    if (ev?.pointerType && ev.pointerType !== 'mouse') return;
    if (ev?.pointerId != null && ev.pointerId !== st.realPointerId) return;

    if (st.slider) st.slider.dispatchEvent(new Event('change', { bubbles: true }));

    st.resizing = false;
    st.realPointerId = null;
    st.centerClient = null;
    st.startDistance = 0;
    st.slider = null;
    st.activeCorner = null;

    Object.values(st.handles).forEach((h) => h.classList.remove('is-resizing'));

    st.controller?.abort();
    st.controller = null;
    requestAnimationFrame(() => updateHandles(st));
  }

  function beginResize(st, e, corner) {
    if (!st?.info || window.innerWidth <= 760 || e.pointerType === 'touch') return;
    if (!imageSelected(st.root)) return;

    const slider = scaleRangeFor(st.root);
    if (!slider) return;

    e.preventDefault();
    e.stopPropagation();

    if (st.controller) st.controller.abort();
    st.controller = new AbortController();

    const center = toClient(st.canvas, st.info.center);
    const startDistance = Math.max(1, Math.hypot(e.clientX - center.x, e.clientY - center.y));
    const startValue = Number(slider.value || 100);

    st.resizing = true;
    st.realPointerId = e.pointerId;
    st.centerClient = center;
    st.startDistance = startDistance;
    st.startValue = Number.isFinite(startValue) ? startValue : 100;
    st.slider = slider;
    st.activeCorner = corner;

    const activeHandle = st.handles[corner];
    Object.values(st.handles).forEach((h) => h.classList.toggle('is-resizing', h === activeHandle));

    const move = (ev) => {
      if (!st.resizing || ev.pointerId !== st.realPointerId || ev.pointerType !== 'mouse') return;
      ev.preventDefault();

      const c = st.centerClient;
      const distance = Math.max(1, Math.hypot(ev.clientX - c.x, ev.clientY - c.y));
      const ratio = distance / st.startDistance;
      const min = Number(st.slider.min || 1);
      const max = Number(st.slider.max || 300);
      const value = clamp(st.startValue * ratio, Number.isFinite(min) ? min : 1, Number.isFinite(max) ? max : 300);

      st.slider.value = String(Math.round(value * 10) / 10);
      st.slider.dispatchEvent(new Event('input', { bubbles: true }));

      if (activeHandle) setHandlePos(activeHandle, { x: ev.clientX, y: ev.clientY });
    };

    const finish = (ev) => finishResize(st, ev);

    window.addEventListener('pointermove', move, { passive: false, signal: st.controller.signal });
    window.addEventListener('pointerup', finish, { signal: st.controller.signal });
    window.addEventListener('pointercancel', finish, { signal: st.controller.signal });
    window.addEventListener('blur', () => finishResize(st), { once: true, signal: st.controller.signal });
  }

  const style = document.createElement('style');
  style.id = 'mf-designer-direct-resize-style';
  style.textContent = `
    .mf-mouse-resize-handle{
      position:fixed;display:none;place-items:center;width:20px;height:20px;padding:0;
      transform:translate(-50%,-50%);z-index:2147482999;
      border:2px solid #fff;border-radius:5px;background:#f58220;
      box-shadow:0 3px 12px rgba(0,0,0,.45);
      touch-action:none;user-select:none;
    }
    .mf-mouse-resize-handle span{
      width:7px;height:7px;border-right:2px solid #111214;border-bottom:2px solid #111214;
      pointer-events:none;
    }
    .mf-mouse-resize-handle--nw,.mf-mouse-resize-handle--se{cursor:nwse-resize}
    .mf-mouse-resize-handle--ne,.mf-mouse-resize-handle--sw{cursor:nesw-resize}
    .mf-mouse-resize-handle--nw span{transform:rotate(135deg)}
    .mf-mouse-resize-handle--ne span{transform:rotate(225deg)}
    .mf-mouse-resize-handle--se span{transform:rotate(-45deg)}
    .mf-mouse-resize-handle--sw span{transform:rotate(45deg)}
    .mf-mouse-resize-handle:hover{
      filter:brightness(1.08);transform:translate(-50%,-50%) scale(1.12)
    }
    .mf-mouse-resize-handle.is-resizing{
      box-shadow:0 0 0 4px rgba(245,130,32,.24),0 4px 14px rgba(0,0,0,.5)
    }
    @media(max-width:760px){.mf-mouse-resize-handle{display:none!important}}
  `;
  if (!document.getElementById(style.id)) document.head.appendChild(style);

  function refresh() {
    activeStates.forEach((st) => {
      if (!st.canvas.isConnected || !st.root.isConnected) {
        Object.values(st.handles || {}).forEach((h) => h?.remove());
        activeStates.delete(st);
        return;
      }

      if (!st.resizing && st.info && st.capturedGen === st.gen && imageSelected(st.root)) updateHandles(st);
      else if (!st.resizing && !imageSelected(st.root)) hide(st);
    });
  }

  window.addEventListener('resize', refresh, { passive: true });
  document.addEventListener('scroll', refresh, { passive: true, capture: true });
  document.addEventListener('input', (e) => {
    const root = e.target?.closest?.(ROOTS);
    if (root && e.target === scaleRangeFor(root)) requestAnimationFrame(refresh);
  }, true);
  document.addEventListener('change', (e) => {
    if (e.target?.closest?.(ROOTS)) requestAnimationFrame(refresh);
  }, true);
  document.addEventListener('click', (e) => {
    if (e.target?.closest?.(ROOTS)) setTimeout(refresh, 0);
  }, true);
  document.addEventListener('shopify:section:load', () => setTimeout(refresh, 50));
})();
